import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { FormContainer, TextInput, SelectInput, CountrySelector } from "../../shared";
import useGlobalMutation from "../../../hooks/useGlobalMutation";
import { CREATE_SITE_ADMIN, CREATE_SYSTEM_ADMIN, CREATE_USER_MANAGER } from "./query";
import { SITE_ADMIN_APP_LIST, RegexPattern, Helper, clearCache, ADMIN_TYPES } from "../../../utils";

const CreateAdmin = ({ userType }) => {
    const currentUserType = Number(useSelector((state) => state.users.currentUser.userType));
    const getUrl = currentUserType === 99 ? "/user-list" : "";
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        watch,
        setError,
        clearErrors,
    } = useForm({
        mode: "onChange",
    });

    const password = watch("password", "");
    const confirmPassword = watch("confirmPassword", "");

    useEffect(() => {
        if (confirmPassword.length > 0 && password !== confirmPassword && !errors.password) {
            setError("confirmPassword", { message: "Passwords do not match" });
        } else {
            clearErrors("confirmPassword");
        }
    }, [password]);

    const { submitMutation, loading } = useGlobalMutation(
        userType === ADMIN_TYPES.USERMANAGER
            ? CREATE_USER_MANAGER
            : userType === ADMIN_TYPES.SYSTEMADMIN
              ? CREATE_SYSTEM_ADMIN
              : CREATE_SITE_ADMIN,
        (cache) => clearCache(cache, "getAdminUserList")
    );

    const submitAction = handleSubmit(async (data) => {
        const { firstName, lastName, email, phone, password, country, appName } = data;

        const variables =
            userType === ADMIN_TYPES.USERMANAGER
                ? {
                      user: {
                          firstName,
                          lastName,
                          email,
                          password,
                          userType: 3,
                          status: 1,
                          country: [],
                      },
                  }
                : {
                      user: {
                          firstName,
                          lastName,
                          email,
                          phone,
                          password,
                          country: [country],
                          appName: [appName],
                      },
                  };

        const dataPath = "createAdminUser.user";
        const successMessage = `${Helper.toTitleCase(userType)} Created Successfully`;
        const isResultNull = await submitMutation(variables, dataPath, successMessage);
        isResultNull === null && reset();
    });
    return (
        <FormContainer
            submitAction={submitAction}
            customClass="custom-shadow p-5"
            loading={loading}
            backUrl={`${getUrl}/${userType}`}
        >
            <h3 className="mb-5">Create {Helper.toTitleCase(userType)}</h3>

            <div className="row">
                <div className="col-md-6">
                    <TextInput
                        id="firstName"
                        labelText="First Name"
                        {...register("firstName", {
                            required: true,
                            pattern: RegexPattern.name,
                        })}
                        isInvalid={!!errors.firstName}
                        required
                    />
                </div>

                <div className="col-md-6">
                    <TextInput
                        id="lastName"
                        labelText="Last Name"
                        {...register("lastName", {
                            required: true,
                            pattern: RegexPattern.nameWithoutSpace,
                        })}
                        isInvalid={!!errors.lastName}
                        required
                    />
                </div>

                <div className="col-md-6">
                    <TextInput
                        id="email"
                        type="email"
                        labelText="Email"
                        {...register("email", {
                            required: true,
                            pattern: RegexPattern.email,
                        })}
                        isInvalid={!!errors.email}
                        required
                    />
                </div>

                <div className="col-md-6">
                    <TextInput
                        id="phone"
                        type="number"
                        labelText="Phone"
                        {...register("phone", {
                            pattern: RegexPattern.number,
                        })}
                        isInvalid={!!errors.phone}
                    />
                </div>

                {userType === ADMIN_TYPES.SITEADMIN && (
                    <>
                        <div className="col-md-6">
                            <Controller
                                name="country"
                                render={({
                                    field: { onChange, value },
                                    fieldState: { invalid },
                                    ref,
                                }) => (
                                    <CountrySelector
                                        id="country"
                                        country={value}
                                        labelText="Country"
                                        selectCountry={onChange}
                                        {...register("country", {
                                            required: true,
                                        })}
                                        required
                                        isInvalid={invalid}
                                        ref={ref}
                                    />
                                )}
                                control={control}
                            />
                        </div>

                        <div className="col-md-6">
                            <SelectInput
                                id="appName"
                                labelText="App Name"
                                options={SITE_ADMIN_APP_LIST}
                                {...register("appName", {
                                    required: true,
                                })}
                                isInvalid={!!errors.appName}
                                required
                            />
                        </div>
                    </>
                )}

                <div className="col-md-6">
                    <Controller
                        name="password"
                        control={control}
                        render={({ field: { ref } }) => (
                            <TextInput
                                id="password"
                                labelText="Password"
                                type="password"
                                isInvalid={!!errors.password}
                                required
                                errorMessage={
                                    (errors.password && errors.password.message) ||
                                    "Invalid Password"
                                }
                                ref={ref}
                                {...register("password", {
                                    required: true,
                                    pattern: RegexPattern.password,
                                })}
                            />
                        )}
                    />
                </div>

                <div className="col-md-6">
                    <Controller
                        name="confirmPassword"
                        control={control}
                        render={({ field: { ref } }) => (
                            <TextInput
                                id="confirmPassword"
                                labelText="Confirm Password"
                                type="password"
                                isInvalid={!!errors.confirmPassword}
                                required
                                errorMessage={
                                    (errors.confirmPassword && errors.confirmPassword.message) ||
                                    "Invalid Password"
                                }
                                ref={ref}
                                {...register("confirmPassword", {
                                    required: true,
                                    validate: (value) =>
                                        value === password || "Passwords do not match",
                                })}
                            />
                        )}
                    />
                </div>
            </div>
        </FormContainer>
    );
};

export default CreateAdmin;
